import { Loader2, Radio } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useStripeTerminal } from '@/context/useStripeTerminal';
import { formatTerminalConnectionError } from '@/utils/formatTerminalConnectionError';
import { appToast } from '@/lib/appToast';
import { cn } from '@/lib/utils';

type TerminalConnectionControlProps = {
  className?: string;
  compact?: boolean;
};

export function TerminalConnectionControl({
  className,
  compact = false,
}: TerminalConnectionControlProps) {
  const terminal = useStripeTerminal();

  const handleConnect = async () => {
    try {
      const connectedReader = await terminal.connectDefaultReader({ resetAttempts: true });
      if (connectedReader) {
        appToast.success(`Connected to ${connectedReader.label}.`);
      }
    } catch (error) {
      appToast.error(formatTerminalConnectionError(error));
    }
  };

  const handleDisconnect = async () => {
    const readerLabel = terminal.connectedReader?.label ?? 'reader';

    try {
      await terminal.disconnectDefaultReader();
      appToast.success(`Disconnected from ${readerLabel}.`);
    } catch (error) {
      appToast.error(formatTerminalConnectionError(error));
    }
  };

  if (terminal.isConfigLoading) {
    return null;
  }

  if (terminal.connectedReader) {
    return (
      <div className={cn('terminal-connection-control', className)}>
        <Button
          aria-label={`Disconnect from ${terminal.connectedReader.label}`}
          className="terminal-connection-badge terminal-connection-badge--connected"
          onClick={() => void handleDisconnect()}
          size="sm"
          type="button"
          variant="secondary"
        >
          <Radio aria-hidden="true" className="h-3.5 w-3.5" />
          <span className="terminal-connection-label">
            {compact ? 'Reader' : terminal.connectedReader.label}
          </span>
        </Button>
      </div>
    );
  }

  if (terminal.isConnecting || terminal.isReconnecting) {
    const connectingLabel = compact
      ? 'Reader'
      : terminal.isReconnecting
        ? 'Reconnecting reader…'
        : 'Connecting reader…';

    return (
      <div className={cn('terminal-connection-control', className)}>
        <Button
          aria-busy="true"
          aria-label={connectingLabel}
          className="terminal-connection-badge terminal-connection-badge--connected pointer-events-none disabled:opacity-100"
          disabled
          size="sm"
          type="button"
          variant="secondary"
        >
          <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />
          <span className="terminal-connection-label">{connectingLabel}</span>
        </Button>
      </div>
    );
  }

  return (
    <div className={cn('terminal-connection-control', className)}>
      <Button
        className="terminal-connection-action"
        onClick={() => void handleConnect()}
        size="sm"
        type="button"
        variant="outline"
      >
        <Radio aria-hidden="true" className="h-4 w-4" />
        <span>{compact ? 'Reader' : terminal.reconnectFailed ? 'Retry reader' : 'Connect reader'}</span>
      </Button>
    </div>
  );
}
